import { useState } from "react";
import { useNavigate, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { fmtError } from "@/lib/api";
import { toast } from "sonner";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Login() {
  const { user, login, idleOut } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const from = location.state?.from ? location.state.from.pathname + (location.state.from.search || "") : "/dashboard";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to={from} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Signed in");
      nav(from, { replace: true });
    } catch (err) {
      toast.error(fmtError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const googleSignIn = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const field = "mt-1 w-full h-11 px-3 border border-[#8a6d3b]/40 rounded-md text-sm bg-[#0f1a24]/70 text-[#f1e6c8] placeholder:text-[#c9b27a]/40 focus:border-[#d4af37] focus:ring-1 focus:ring-[#d4af37] outline-none";

  return (
    <div className="min-h-screen bg-[#0b131b] lg:grid lg:grid-cols-2" data-testid="login-page">
      {/* Left: full artwork, uncropped */}
      <div className="relative h-[42vh] lg:h-screen bg-[#0b131b] flex items-center justify-center overflow-hidden">
        <img src="/login-art.webp" alt="" className="h-full w-full object-contain" data-testid="login-art" />
      </div>

      {/* Right: login only */}
      <div className="flex items-center justify-center p-4 sm:p-8 lg:min-h-screen">
        <div className="w-full max-w-sm rounded-xl border border-[#d4af37]/30 bg-[#0f1a24] shadow-[0_20px_60px_rgba(0,0,0,0.55)] p-6 sm:p-7" data-testid="login-card">
          <button
            data-testid="google-signin-btn"
            onClick={googleSignIn}
            className="w-full h-11 border border-[#d4af37]/40 rounded-md bg-[#f1e6c8] hover:bg-white flex items-center justify-center gap-3 text-sm font-medium text-slate-900 transition-colors"
          >
            <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.5 29.3 4.5 24 4.5 12.7 4.5 3.5 13.7 3.5 25S12.7 45.5 24 45.5 44.5 36.3 44.5 25c0-1.5-.2-3-.4-4.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.9 19 13 24 13c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.5 29.3 4.5 24 4.5 16.4 4.5 9.9 8.7 6.3 14.7z"/><path fill="#4CAF50" d="M24 45.5c5.2 0 9.9-2 13.4-5.2l-6.2-5.2c-2 1.4-4.5 2.2-7.2 2.2-5.3 0-9.7-3.4-11.3-8l-6.5 5C9.7 41.1 16.3 45.5 24 45.5z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.5l6.2 5.2c-.4.4 6.6-4.8 6.6-13.7 0-1.5-.2-3-.4-4.5z"/></svg>
            Continue with Google
          </button>

          <div className="my-5 flex items-center gap-3 text-[10px] uppercase tracking-[0.2em] text-[#c9b27a]/70">
            <div className="flex-1 h-px bg-[#d4af37]/30"/>or<div className="flex-1 h-px bg-[#d4af37]/30"/>
          </div>

          <form onSubmit={submit} className="space-y-3">
            {idleOut && <div data-testid="idle-logout-notice" className="text-xs bg-amber-50/90 border border-amber-200 text-amber-900 rounded-md px-3 py-2">You were signed out after 5 minutes of inactivity. Please sign in again.</div>}
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#c9b27a]">Email</label>
              <input data-testid="login-email" type="email" required autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} className={field}/>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#c9b27a]">Password</label>
              <input data-testid="login-password" type="password" required autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} className={field}/>
            </div>
            <button data-testid="login-submit" type="submit" disabled={busy}
              className="w-full h-11 bg-[#d4af37] hover:bg-[#e2c15a] disabled:opacity-60 text-[#0b131b] font-bold text-xs uppercase tracking-[0.2em] rounded-md transition-colors">
              {busy ? "Signing in…" : "Sign in"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
